/* eslint-disable react/no-this-in-sfc */
import AnchorJS from 'anchor-js';
import hljs from 'highlight.js/lib/core';
import c from 'highlight.js/lib/languages/c';
import java from 'highlight.js/lib/languages/java';
import javascript from 'highlight.js/lib/languages/javascript';
import php from 'highlight.js/lib/languages/php';
import python from 'highlight.js/lib/languages/python';
import shell from 'highlight.js/lib/languages/shell';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import mediumZoom from 'medium-zoom';
import Head from 'next/head';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Avatar from '../components/avatar';
import Link from '../components/link';
import MetaLinks from '../components/metaLinks';
import MetaSocial from '../components/metaSocial';
import NoSSR from '../components/noSSR';
import PinToFav from '../components/pinToFav';
import Rate from '../components/rate';
import ApiError from '../error/ApiError';
import { useTranslation } from '../i18n';
import theme from '../layouts/theme';
import { Router } from '../routes';
import { encodeHtml, getBasePath, getBaseUrl } from '../utils';
import { getFingerprint, usePinnedArticleIds } from '../utils/articleUtils';
import fetch from '../utils/fetch';

hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('java', java);
hljs.registerLanguage('php', php);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('c', c);
hljs.registerLanguage('shell', shell);

const i18nNs = ['common'];

const anchors = new AnchorJS({
  placement: 'left',
});

function ArticleComments({ integrations, fullUrl }) {
  return (
    <div className="articleComments">
      {integrations?.disqus?.enabled && <div id="disqus-comments" />}
      {integrations?.commento?.enabled && (
        <>
          <div id="commento" />
          <script defer src="https://cdn.commento.io/js/commento.js" />
        </>
      )}
      {integrations?.discourse?.enabled && (
        <>
          <div id="discourse-comments" />
          <script type="text/javascript">
            {`
        DiscourseEmbed = { discourseUrl: '${integrations.discourse.url}/',
        discourseEmbedUrl: '${fullUrl}' };

        (function() {
        var d = document.createElement('script'); d.type = 'text/javascript'; d.async = true;
        d.src = DiscourseEmbed.discourseUrl + 'javascripts/embed.js';
        (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(d);
        })();`}
          </script>
        </>
      )}
    </div>
  );
}

function Article({ desk, article, query }) {
  const [rate, setRate] = useState(null);
  const rootRef = useRef();
  const { t } = useTranslation(['common']);

  const onRate = useCallback(
    async newRate => {
      setRate(newRate);
      const fingerprint = await getFingerprint();
      fetch(`/articles/${article._id}/rate/${newRate}`, {
        headers: { 'X-FP': fingerprint },
      }).catch(() => {});
    },
    [article?._id],
  );

  useEffect(() => {
    if (!article) return;
    getFingerprint().then(fingerprint => {
      fetch(`/articles/${article._id}/view`, { headers: { 'X-FP': fingerprint } }).catch(() => {});
      fetch(`/articles/${article._id}/rate`, { headers: { 'X-FP': fingerprint } })
        .then(async resp => {
          const prevRate = await resp.json();
          setRate(prevRate.rate);
        })
        .catch(() => {});
    });
    if (rootRef.current) {
      rootRef.current.querySelectorAll('pre code').forEach(block => {
        hljs.highlightBlock(block);
      });
      anchors.add(
        '.articleBody h1, .articleBody h2, .articleBody h3, .articleBody h4, .articleBody h5, .articleBody h6',
      );
    }
    if (query.slug !== article.slug) {
      Router.replaceRoute(`${getBasePath(query)}/articles/${article._id}-${article.slug}`);
    }
  }, [article?._id]);

  const pathAfterLang = `/articles/${article?._id}-${article?.slug}`;
  const fullUrl = `${getBaseUrl(query)}${pathAfterLang}`;
  const title = `${article?.title}${
    desk.languageFields.title ? ` | ${desk.languageFields.title}` : ''
  }`;

  // handle integrations
  useEffect(() => {
    const { integrations, _id: deskId } = desk;
    if (integrations?.disqus?.enabled && integrations.disqus.shortname) {
      // Disqus integration
      window.disqusLoader('#disqus-comments', {
        scriptUrl: `//${integrations.disqus.shortname}.disqus.com/embed.js`,
        laziness: 1, // load when it's almost in viewport
        throttle: 250, // check every 250ms
        disqusConfig() {
          this.page.title = title;
          this.page.url = fullUrl;
          this.page.identifier = `${deskId}${pathAfterLang}`;
        },
      });
    }
  }, [desk.integrations, desk._id]);

  const hasUser = !!article?.user?.name;
  const writtenByText = hasUser
    ? t('writtenByLabel1', { user1: encodeHtml(article?.user.name), count: 1 })
    : '';

  useEffect(() => {
    const zoom = mediumZoom('[data-zoomable]');
    return () => zoom.detach();
  }, []);

  const articleBody = useMemo(() => {
    if (!article?.bodyHtml) return '';
    // if (article.bodyHtml.includes('<table>')) {
    //   // Regex pattern to match <table> tags
    //   const tableRegex = /(<table\b[^>]*>[\s\S]*?<\/table>)/g;
    //   // Wrap table elements with div elements to prevent overflow bugs
    //   article.bodyHtml = article.bodyHtml.replace(
    //     tableRegex,
    //     '<div style="overflow-x:scroll;">$1</div>',
    //   );
    // }
    // put header in table of contents
    if (!article.bodyHtml.includes('<table-of-contents>')) {
      return article.bodyHtml;
    }
    return article.bodyHtml.replace(
      '<table-of-contents>',
      `<table-of-contents><div class="tocTitle">${t('tableOfContents')}</div>`,
    );
  }, [article?.bodyHtml, t]);

  if (!article) {
    Router.pushRoute(getBasePath(query));
    return null;
  }
  const [pinnedArticleIds] = usePinnedArticleIds();
  const isPinned = pinnedArticleIds.includes(article._id);

  return (
    <div className="root" ref={rootRef}>
      <Head>
        <title key="title">{title}</title>
      </Head>
      <MetaLinks path={pathAfterLang} language={article.language} alternates={false} />
      <MetaSocial
        title={article.title}
        description={article.spoiler}
        type="article"
        image={desk.socialMediaImgUrl}
      />
      <div className="content">
        <div className="breadcrumbs lightText">
          <span className="darkText hoverable">
            <Link route="home">
              <a>{t('allCollections')}</a>
            </Link>
          </span>
          {article.collection && (
            <>
              <i className="fas fa-angle-right angle" />
              <span className="darkText hoverable">
                <Link
                  route="collection"
                  params={{ _id: article.collection._id, slug: article.collection.slug }}
                >
                  <a>{article.collection.title}</a>
                </Link>
              </span>
            </>
          )}
          <i className="fas fa-angle-right angle" />
          <span>{article.title}</span>
        </div>

        <article className="container">
          <div className="content containerInner">
            <header className="info">
              <div className="titleContainer">
                <NoSSR>
                  <div className="pinButton" style={{ opacity: isPinned ? 1 : undefined }}>
                    <PinToFav articleId={article._id} />
                  </div>
                </NoSSR>
                <h1>{article.title}</h1>
              </div>
              <p>{article.spoiler}</p>
              <div className="bottom">
                {hasUser && (
                  <div className="bottomLeft">
                    <Avatar user={article.user} />
                  </div>
                )}
                <div className="bottomRight lightText">
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: writtenByText }} />
                  <div>
                    {t('lastUpdate')}:{' '}
                    {new Date(article.updatedAt).toLocaleDateString(query.language)}
                  </div>
                </div>
              </div>
            </header>

            {/* eslint-disable-next-line react/no-danger */}
            <main className="articleBody" dangerouslySetInnerHTML={{ __html: articleBody }} />

            <footer className="rateSection">
              <Rate rate={rate} onRate={onRate} />
            </footer>

            <ArticleComments integrations={desk.integrations} fullUrl={fullUrl} />
          </div>
        </article>
      </div>

      <style jsx global>
        {`
          html {
            ${articleBody.includes('<table-of-contents>') ? 'scroll-behavior: smooth;' : ''}
          }
        `}
      </style>

      <style jsx>
        {`
          .root {
            margin-top: -20px;
          }
          .breadcrumbs {
            display: block;
            word-break: break-word;
            font-size: 14px;
          }
          .angle {
            margin: 0 5px;
          }
          .container {
            background: #fff;
            margin-top: 15px;
            padding: 30px;
          }
          .containerInner {
            width: 640px;
          }
          .info h1 {
            color: #212121;
            margin: 0;
            font-size: 32px;
            font-weight: 400;
          }
          .info p {
            margin: 0 0 14px;
            color: ${theme.lightText};
          }
          .info .bottom {
            display: flex;
            flex-flow: row nowrap;
          }
          .info .bottomLeft {
            margin-right: 10px;
          }
          .info .bottomRight {
            font-size: 13px;
            line-height: 1.3;
          }
          .info .titleContainer {
            display: flex;
            align-items: center;
            font-size: 32px;
            margin: 0 0 0.3em;
            position: relative;
          }
          .titleContainer .pinButton {
            display: inline-flex;
            position: absolute;
            transform: translateX(-100%);
            left: -0.3em;
            opacity: 0;
            transition: opacity 0.2s ease;
            font-size: 20px;
          }
          .titleContainer:hover .pinButton {
            opacity: 1;
          }
          .container:hover .pinHover {
            display: inline-block;
          }
          .articleBody {
            margin-top: 30px;
            word-break: break-word;
          }
          .articleBody :global(a) {
            color: ${desk.color};
          }
          .articleBody :global(li) {
            margin: 0.5em 0;
          }
          .articleBody :global(img) {
            display: block;
            margin: 1em auto;
            max-height: 650px;
          }
          .articleBody :global(hr) {
            display: block;
            height: 1px;
            border: 0;
            border-top: 1px solid #c5ccd3;
            margin: 1em 0;
            padding: 0;
          }
          .articleBody :global(blockquote) {
            background: #f9f9f9;
            border-left: 0.3em solid #c5ccd3;
            margin: 1.5em 0px;
            padding: 0.5em 1em;
          }
          .articleBody :global(blockquote p:first-child) {
            margin-top: 0;
          }
          .articleBody :global(blockquote p:last-child) {
            margin-bottom: 0;
          }
          .rateSection {
            background: ${theme.bgGray};
            margin-top: 40px;
            border-radius: 4px;
            padding: 15px 20px;
            text-align: center;
          }
          .articleComments {
            margin-top: 40px;
          }
          @media screen and (max-width: 600px) {
            .container {
              padding: 25px 20px;
            }
            .titleContainer .pinButton {
              display: none;
            }
          }
          @media screen and (max-width: 480px) {
            .info h1 {
              font-size: 28px;
            }
          }

          :global(table-of-contents) {
            display: block;
            padding: 1em;
            margin: 1em 0;
            background: #f9f9f9;
          }
          :global(table-of-contents ul) {
            margin-bottom: 0;
          }
          :global(table-of-contents .tocTitle) {
            font-size: 1.25rem;
            font-weight: 600;
          }
          :global(.tableWrapper) {
            overflow: auto;
          }
          // always show the scrollbar
          :global(.tableWrapper::-webkit-scrollbar) {
            width: 6px;
            height: 6px;
          }
          :global(.tableWrapper::-webkit-scrollbar-thumb) {
            background: rgba(0, 0, 0, 0.12);
            border-radius: 3px;
            box-shadow: inset 0 0 5px rgba(0, 21, 41, 0.05);
          }
          :global(.tableWrapper::-webkit-scrollbar-track) {
            background: rgba(0, 0, 0, 0.06);
            border-radius: 3px;
            box-shadow: inset 0 0 5px rgba(0, 21, 41, 0.05);
          }
        `}
      </style>
    </div>
  );
}

Article.getInitialProps = async ctx => {
  const { query } = ctx;
  if (!query._id || Number.isNaN(Number(query._id))) {
    throw new ApiError('Article not found', 404);
  }
  // sanitize html when it's on Infoset domain, to protect us from XSS attacks with HTML embed
  const sanitize = ctx.domain === process.env.HELPDESK_DOMAIN && ctx.desk.slug !== 'infoset';
  const article = await fetch(`/articles/${query._id}`, { query: { sanitize } }).then(resp =>
    resp.json());
  if (!article) {
    throw new ApiError('Article not found', 404);
  }
  return {
    query,
    article,
    namespacesRequired: i18nNs,
  };
};

export default Article;
